import { CustomerType, EnergyType, State } from '@energy-retailer/constants';
import { z } from 'zod';
import {
  ElectricityDistributorSchema,
  distributorSchema,
  getDistributorsByPostCode,
} from '../distributor-schema';
import { nmiAddressSchema } from '../nmi-address-schema';
import {
  customerType,
  evSupportingNTCs,
  mapNTC,
  ntcMapping,
  smartMeterInstallationTypesAER,
  smartMeterInstallationTypesVIC,
  validNTC,
} from '../ntc-schema';
import { fullPropertyAddressSchema } from '../full-property-address-schema';
import { getIsGasAvailable } from '../utils/get-is-gas-available';
import isEmpty from 'lodash/isEmpty';

export const postcodeSchema = z
  .string()
  .max(4)
  .transform((val, ctx) => {
    const parsed = parseInt(val, 10);

    if (isNaN(parsed)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Postcode must be a number',
      });

      return z.NEVER;
    }

    return parsed;
  });

export const meterSchema = z.object({
  controlledLoad: z.string(),
  networkTariffCode: z.string(),
  installationTypeCode: z.string(),
});

const smartMeter = z
  .object({
    address: nmiAddressSchema,
    meter: z.array(meterSchema),
  })
  .transform(({ address, meter }) => {
    const state = address.state;

    if (state === State.VIC) {
      return meter.some((m) =>
        smartMeterInstallationTypesVIC.includes(m.installationTypeCode)
      );
    }

    return meter.some((m) =>
      smartMeterInstallationTypesAER.includes(m.installationTypeCode)
    );
  });

const evMeter = z
  .object({
    meter: z.array(meterSchema),
  })
  .transform(({ meter }) => {
    return meter.some((item) => {
      return evSupportingNTCs.includes(item.networkTariffCode);
    });
  });

const touMeter = z
  .object({
    meter: z.array(meterSchema),
  })
  .transform(({ meter }) => {
    return meter.some((item) => {
      const ntcMappedValue = mapNTC(
        item.networkTariffCode as keyof typeof ntcMapping
      );

      if (!ntcMappedValue) {
        return false;
      }

      return /^Time of use(?:.*)$/.test(ntcMappedValue);
    });
  });

export type MeterSchema = z.infer<typeof meterSchema>;

export const propertySchema = z
  .object({
    checksum: z.string().optional(),
    maskedNmi: z.string().optional(),
    encryptedNmi: z.string().optional(),
    address: fullPropertyAddressSchema,
    activeDistributor: z.string().optional(),
    meter: z.array(meterSchema).default([]),
  })
  .transform((value) => {
    const distributors = getDistributorsByPostCode.parse(
      value.address.postcode
    )!;

    const electricityDistributors = distributors.filter(
      (distributor) => distributor.energyType === EnergyType.Electricity
    );

    const gasDistributors = distributors.filter(
      (distributor) => distributor.energyType === EnergyType.Gas
    );

    const formattedGasDistributors = distributorSchema
      .array()
      .parse(gasDistributors);

    // if we don't have an activeDistributor or a valid meter, MSATS could not find this property
    // (either due to an error or the property is not in the MSATS database)
    // return a default set of data
    if (!value.activeDistributor || value.meter.length === 0) {
      return {
        address: value.address,
        meter: [] as MeterSchema[],
        hasSmartMeter: false,
        hasTOUMeter: false,
        supportsEV: false,
        electricityDistributors: distributorSchema
          .array()
          .parse(electricityDistributors),
        gasDistributors: formattedGasDistributors,
        isGasAvailable: getIsGasAvailable(
          value.address.state,
          value.address.postcode,
          !isEmpty(formattedGasDistributors)
        ),
      };
    }

    // find the first non-controlled load meter, and use that to determine the `customerType`
    const ntcMeter = value.meter.find(
      (m) =>
        m.controlledLoad === 'NO' &&
        validNTC.includes(m.networkTariffCode as any)
    );

    const derivedCustomerType = ntcMeter
      ? customerType.parse(ntcMeter.networkTariffCode)
      : undefined;

    // if we do have an activeDistributor, filter the electricity distributors
    const filteredElectricityDistributors = value.activeDistributor
      ? (electricityDistributors as ElectricityDistributorSchema[])
        .filter((distributor) => distributor.id === value.activeDistributor)
        .map((distributor) => {
          const selectedDistributor = distributorSchema.parse(
            distributor
          ) as ElectricityDistributorSchema;

          if (!derivedCustomerType) {
            // if no customer type can be derived, return both
            // (this will always result in no plans being returned)
            return {
              ...selectedDistributor,
              meter: {
                ...selectedDistributor.meter,
                [CustomerType.Residential]: value.meter,
                [CustomerType.Business]: value.meter,
              },
            };
          }

          // replace the default meter information with the MSATS meter information
          return {
            ...selectedDistributor,
            meter: {
              ...selectedDistributor.meter,
              [derivedCustomerType]: value.meter,
            },
          };
        })
      : distributorSchema.array().parse(electricityDistributors);

    const hasSmartMeter = smartMeter.parse(value);
    const hasEVMeter = evMeter.parse(value);
    const hasTOUMeter = touMeter.parse(value);

    return {
      address: value.address,
      encryptedNmi: value.encryptedNmi,
      maskedNmi: `${value.maskedNmi}${value.checksum}`,
      hasSmartMeter,
      hasTOUMeter,
      supportsEV: hasSmartMeter && hasEVMeter,
      customerType: derivedCustomerType,
      electricityDistributors: filteredElectricityDistributors,
      gasDistributors: formattedGasDistributors,
      isGasAvailable: getIsGasAvailable(
        value.address.state,
        value.address.postcode,
        !isEmpty(formattedGasDistributors)
      ),
    };
  });

export type PropertySchema = z.output<typeof propertySchema>;

export { getIsGasAvailable } from '../utils/get-is-gas-available';
